/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useSelector, useDispatch } from 'react-redux';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Button from '@salesforce/design-system-react/components/button';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Spinner from '@salesforce/design-system-react/components/spinner';
import InventoryRequestIdCell from './DataTableCells/InventoryRequestIdCell';
import ResetCell from './DataTableCells/ResetCell';
import InventoryRequestStatusCell from './DataTableCells/InventoryStatusCell';
import InventoryRequestDateTImeCell from './DataTableCells/InventoryDateTImeCell';
import Inventory3PL from './DataTableCells/Inventory3PL';
import './index.scss';
import InventoryQueueHeader from './InventoryQueueHeader';
import debounced from '../../../util/debounced';
import useSnackBar from '../../../util/customHooks/useSnackBar';
import SnackBar from '../../Shared/SnackBar';
import InventoryRequestsAssignDataCell from './DataTableCells/InventoryAssignDataCell';
import AccountCell from './DataTableCells/AccountCell';
import { GET_INVENTORY_REVIEW_LIST } from '../../../graphql/getInventoryReviewList';
import ASSIGN_INVENTORY_REQUEST from '../../../graphql/mutations/assignInventoryRequest';
import {
  NEXT_DATA,
  PREV_DATA,
  NOTIFY_TYPE,
  INVENTORY_REQUEST_ASYNC_MESSAGES,
  SHOW_AGENCY_RECORDS,
  SHOW_STRYKER_RECORDS,
  DEBOUNCE_SEARCH_TIMEOUT,
  INVENTORY_SORT_KEYS,
  REVIEW_QUEUE_SORT_COLUMNS,
  USAGE_TYPE,
  INVENTORY_RETURN,
  STANDARD_RETURN,
  PICKUP_RETURN,
  LIMIT,
  MIN_SEARCH_LENGTH,
} from '../../../util/constants';
import { getUSDString } from '../../../util/utilityFunctions';
import { getFormattedHoldReasons } from '../../../util/formatHoldReasons';
import { readSortFromStorage, writeSortToStorage } from '../../../util/listSortStorage';
import { getUserInfo } from '../../../store/ducks/userInfo';
import FilterTagReviewRequest from '../../Shared/FilterTagReviewRequest';
import { GET_FILTER_PRESETS } from '../../../graphql/getFilterPresets';
import InventoryQueueFilter from './InventoryQueueFilter';
import useSubmissionStatuses from './hooks/useSubmissionStatuses';
import {
  setInventoryReviewFilters,
  getInventoryReviewFilters,
  clearInventoryReviewFilters,
} from '../../../store/ducks/inventoryReviewFilters';

const buildQueueFilters = (baseFilters: object | null | undefined, searchText: string): any =>
  searchText && searchText.length >= MIN_SEARCH_LENGTH
    ? { ...baseFilters, searchText }
    : baseFilters;

// The record kinds whose submissions go through the submission ledger, so their queue rows can show
// the in-flight icon. Return rows arrive typed as Standard Return or Pickup Return.
const SUBMISSION_TRACKED_QUEUE_TYPES = [
  USAGE_TYPE.INVENTORY_REQUEST,
  INVENTORY_RETURN,
  STANDARD_RETURN,
  PICKUP_RETURN,
];
const isSubmissionTrackedQueueType = (type?: string): boolean =>
  !!type && SUBMISSION_TRACKED_QUEUE_TYPES.includes(type);

interface Props {
  action: string;
}

interface InventoryTotalCostDataCellProps {
  item?: {
    totalcost?: string;
  };
}

interface Sort {
  sortColumn: string;
  sortColumnDirection: {
    [key: string]: string;
  };
}

interface SelectedData {
  id: string;
  label: string;
  __typename?: string;
}

interface Filter {
  status: SelectedData[];
  salesReps: SelectedData[];
  coveringReps: SelectedData[];
  account: SelectedData[];
  branch: SelectedData[];
  franchise: SelectedData[];
  surgeon: SelectedData[];
  procedureDetail: SelectedData[];
  procedureType?: SelectedData[];
  startDate: Date | string;
  endDate: Date | string;
  usage: string;
  eventType: string[];
  filterExternalId?: string;
  filterName?: string;
  searchText?: string;
}

interface FilterPreset {
  filterValues: Filter;
  filterExternalId?: string;
  filterName?: string;
}

interface DynamicValue {
  [key: string]: any;
}

const InventoryTotalCostDataCell: FC<InventoryTotalCostDataCellProps> = ({
  item = {
    totalcost: null,
  },
}) => {
  const { totalcost } = item;
  return (
    <DataTableCell title={totalcost || ''}>
      <p>{totalcost ? getUSDString(totalcost.toString()) : '0.00'}</p>
    </DataTableCell>
  );
};

// CustomDataTableCell must have the same displayName as DataTableCell or it will be ignored.
InventoryRequestIdCell.displayName = DataTableCell.displayName;
InventoryRequestStatusCell.displayName = DataTableCell.displayName;
InventoryRequestsAssignDataCell.displayName = DataTableCell.displayName;
AccountCell.displayName = DataTableCell.displayName;
InventoryRequestDateTImeCell.displayName = DataTableCell.displayName;
InventoryTotalCostDataCell.displayName = DataTableCell.displayName;
Inventory3PL.displayName = DataTableCell.displayName;
ResetCell.displayName = DataTableCell.displayName;

const InventoryQueue: FC<Props> = ({ action }) => {
  const [getQueueList, { data: inventoryQueueData, refetch, loading }] = useLazyQuery(
    GET_INVENTORY_REVIEW_LIST,
    {
      fetchPolicy: 'no-cache',
    }
  );
  const [assignToOps, { data: assignRespone, loading: isLoading }] = useMutation(
    ASSIGN_INVENTORY_REQUEST
  );
  const [inventoryQueueList, setState] = useState([]);
  const { open, notification, openSnackBar } = useSnackBar();
  const [offsetRequestReview, setOffsetRequestReview] = useState(0);
  const [offsetRequestAssign, setOffsetRequestAssign] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState<object | null>(null);
  const userInfo = useSelector(getUserInfo);

  // Sort storage initialization
  const [sortObj, setSortObj] = useState<Sort | undefined>(() => {
    const storageKey = INVENTORY_SORT_KEYS[action];
    const stored = readSortFromStorage(storageKey, REVIEW_QUEUE_SORT_COLUMNS);
    return stored
      ? {
          sortColumn: stored.sortColumn,
          sortColumnDirection: { [stored.sortColumn]: stored.sortDirection },
        }
      : undefined;
  });
  const sortObjRef = React.useRef<Sort | undefined>(sortObj);
  sortObjRef.current = sortObj;

  const [selectedFilterApiValues, setSelectedFilterApiValues] = useState(null);
  // The search text last sent to the server; shorter input sends nothing, so the list still reflects this.
  const lastSentSearchRef = React.useRef('');
  const [searchValue, setSearchValue] = useState('');
  const [filterEventModalVisible, setFilterEventModalVisible] = useState(false);
  const [isEditFilter, setIsEditFilter] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [updatedSelectedFilter, setUpdatedSelectedFilter] = useState<
    FilterPreset | DynamicValue | null
  >(null);
  const [selectedFilter, setSelectedFilter] = useState<FilterPreset | DynamicValue | null>(null);
  const dispatch = useDispatch();

  // Memoize external IDs to prevent unnecessary re-renders
  const inventoryRequestCaseExternalIds = useMemo(
    () =>
      Array.from(
        new Set(
          inventoryQueueList
            .filter((item: any) => isSubmissionTrackedQueueType(item?.type))
            .map((item: any) => item?.caseExternalId)
            .filter(Boolean)
        )
      ),
    [inventoryQueueList]
  );

  const { submissionStatusByCase, error: submissionStatusError } = useSubmissionStatuses(
    inventoryRequestCaseExternalIds,
    action.toUpperCase() === 'REVIEW'
  );
  const lastSubmissionStatusErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (submissionStatusError) {
      const errorKey = submissionStatusError.message || 'submission-status-query-error';
      if (lastSubmissionStatusErrorRef.current !== errorKey) {
        openSnackBar(NOTIFY_TYPE.ERROR, INVENTORY_REQUEST_ASYNC_MESSAGES.STATUS_QUERY_ERROR);
        lastSubmissionStatusErrorRef.current = errorKey;
      }
    } else {
      lastSubmissionStatusErrorRef.current = null;
    }
  }, [submissionStatusError, openSnackBar]);

  // Memoize items with submission status to prevent unnecessary re-renders
  const itemsWithSubmissionStatus = useMemo(
    () =>
      inventoryQueueList.map((item: any) => ({
        ...item,
        submissionStatusRecord:
          isSubmissionTrackedQueueType(item?.type) && item?.caseExternalId
            ? submissionStatusByCase[item.caseExternalId] ?? null
            : null,
      })),
    [inventoryQueueList, submissionStatusByCase]
  );

  useEffect(() => {
    const s = sortObjRef.current;
    const sortVars = s
      ? { orderBy: s.sortColumn, orderSortType: s.sortColumnDirection[s.sortColumn] }
      : {};
    const filters = buildQueueFilters(appliedFilters, lastSentSearchRef.current);
    getQueueList({
      variables: {
        limit: LIMIT,
        offset: 0,
        type: 'REVIEW',
        filters,
        ...sortVars,
      },
    });
  }, [action, getQueueList, appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: filterPresets, refetch: refetchFilterPresets } = useQuery(GET_FILTER_PRESETS, {
    variables: {
      userId: userInfo?.id,
      type: 'Review Request',
    },
  });

  const filterData = filterPresets?.getFilterPresets;

  useEffect(() => {
    if (assignRespone && assignRespone.assignInventoryRequest.message === NOTIFY_TYPE.SUCCESS) {
      openSnackBar(NOTIFY_TYPE.SUCCESS, 'Reset Assignment Successfully');
      refetch();
    }
  }, [assignRespone, refetch, openSnackBar]);

  useEffect(() => {
    if (inventoryQueueData && inventoryQueueData.getInventoryReviewList) {
      const formattedData = inventoryQueueData.getInventoryReviewList.map((item: any) => {
        return {
          ...item,
          userHoldReasons: getFormattedHoldReasons(item.userHoldReasons),
        };
      });

      setState(formattedData);
    }
  }, [inventoryQueueData]);

  // Reload sort from storage when action (Review/Assign) changes
  useEffect(() => {
    const key = INVENTORY_SORT_KEYS[action];
    if (key) {
      const savedSort = readSortFromStorage(key, REVIEW_QUEUE_SORT_COLUMNS);
      if (savedSort) {
        setSortObj({
          sortColumn: savedSort.sortColumn,
          sortColumnDirection: {
            [savedSort.sortColumn]: savedSort.sortDirection,
          },
        });
      } else {
        setSortObj(undefined);
      }
    }
  }, [action]);

  // This function fetches the next and previous records of the events list
  const handlePaginationButtonClicked = (type: string): void => {
    let finalOffset;
    const s = sortObjRef.current;
    const sortVars = s
      ? { orderBy: s.sortColumn, orderSortType: s.sortColumnDirection[s.sortColumn] }
      : {};

    if (action.toUpperCase() === 'REVIEW') {
      if (type === NEXT_DATA) {
        finalOffset = offsetRequestReview + LIMIT;
      } else {
        finalOffset = offsetRequestReview - LIMIT;
      }
      const filters = buildQueueFilters(appliedFilters, lastSentSearchRef.current);
      getQueueList({
        variables: {
          limit: LIMIT,
          offset: finalOffset,
          type: 'REVIEW',
          filters,
          ...sortVars,
        },
      });
      setOffsetRequestReview(finalOffset);
    } else if (action.toUpperCase() === 'ASSIGN') {
      if (type === NEXT_DATA) {
        finalOffset = offsetRequestAssign + LIMIT;
      } else {
        finalOffset = offsetRequestAssign - LIMIT;
      }
      const filters = buildQueueFilters(appliedFilters, lastSentSearchRef.current);
      getQueueList({
        variables: {
          limit: LIMIT,
          offset: finalOffset,
          type: 'ASSIGN',
          filters,
          ...sortVars,
        },
      });
      setOffsetRequestAssign(finalOffset);
    }
  };

  const applyFilters = (appliedFilter: any): void => {
    setAppliedFilters(appliedFilter);
    setSelectedFilter(appliedFilter);
    setOffsetRequestAssign(0);
    setOffsetRequestReview(0);
    const filters = appliedFilter || undefined;
    // eslint-disable-next-line no-underscore-dangle
    delete filters?.__typename;

    setSelectedFilterApiValues(filters);
    const s = sortObjRef.current;
    // Applying or removing filters clears the search, so page 1 is requested without it too.
    lastSentSearchRef.current = '';
    setSearchValue('');
    getQueueList({
      variables: {
        limit: LIMIT,
        offset: 0,
        type: 'REVIEW',
        ...(s ? { orderBy: s.sortColumn, orderSortType: s.sortColumnDirection[s.sortColumn] } : {}),
        filters,
      },
    });
    dispatch(
      setInventoryReviewFilters(JSON.stringify({ filterValues: appliedFilter, filterName: '' }))
    );
  };

  const setSearch = (searchedItem: string): void => {
    const s = sortObjRef.current;
    const sortVars = s
      ? { orderBy: s.sortColumn, orderSortType: s.sortColumnDirection[s.sortColumn] }
      : {};

    if (searchedItem && searchedItem.length >= MIN_SEARCH_LENGTH) {
      lastSentSearchRef.current = searchedItem;
      const filtersWithSearch = buildQueueFilters(selectedFilterApiValues, searchedItem);
      getQueueList({
        variables: {
          limit: LIMIT,
          offset: 0,
          type: 'REVIEW',
          filters: filtersWithSearch,
          ...sortVars,
        },
      });
      setOffsetRequestReview(0);
      setOffsetRequestAssign(0);
    } else if (searchedItem === '') {
      lastSentSearchRef.current = '';
      const filtersWithoutSearch = selectedFilterApiValues
        ? (() => {
            const { searchText, ...rest } = selectedFilterApiValues as any;
            return Object.keys(rest).length > 0 ? rest : null;
          })()
        : null;

      getQueueList({
        variables: {
          limit: LIMIT,
          offset: 0,
          type: 'REVIEW',
          filters: filtersWithoutSearch,
          ...sortVars,
        },
      });
      setOffsetRequestReview(0);
      setOffsetRequestAssign(0);
    }
    // If search text is between 0-2 chars, don't make API call
  };

  const setSearchRef = useRef(setSearch);
  setSearchRef.current = setSearch;
  const debouncedFetchItems = useMemo(
    () => debounced(DEBOUNCE_SEARCH_TIMEOUT, (value: string) => setSearchRef.current(value)),
    []
  );

  const handleSearch = useCallback(
    (_event: { persist: () => void }, searchedItem: any) => {
      const { value } = searchedItem;
      _event.persist();
      setSearchValue(value);
      debouncedFetchItems(value);
    },
    [debouncedFetchItems]
  );

  const handleSort = (sortColumn: any): void => {
    const sortProperty = sortColumn.property;
    const { sortDirection } = sortColumn;
    const sort = {
      sortColumn: sortProperty,
      sortColumnDirection: { [sortProperty]: sortDirection },
    };
    setSortObj(sort);
    setState(currentItems =>
      [...currentItems].sort((leftItem, rightItem) => {
        const leftValue = String(leftItem[sortProperty] ?? '');
        const rightValue = String(rightItem[sortProperty] ?? '');
        const comparison = leftValue.localeCompare(rightValue);
        return sortDirection === 'desc' ? -comparison : comparison;
      })
    );
    writeSortToStorage(INVENTORY_SORT_KEYS[action], sortProperty, sortDirection);
    const filters = buildQueueFilters(appliedFilters, lastSentSearchRef.current);
    getQueueList({
      variables: {
        limit: LIMIT,
        offset: 0,
        type: 'REVIEW',
        orderBy: sortProperty,
        orderSortType: sortDirection,
        filters,
      },
    });
    setOffsetRequestReview(0);
    setOffsetRequestAssign(0);
  };

  const handleReset = (externalId: any): void => {
    assignToOps({
      variables: {
        externalId,
        assignedUser: '',
      },
    });
  };

  const resetFilters = useCallback((): void => {
    dispatch(clearInventoryReviewFilters());
    setAppliedFilters(null);
    setSelectedFilter(null);
    setUpdatedSelectedFilter(null);
    setIsEditFilter(false);
    setSelectedFilterApiValues(null);
    const s = sortObjRef.current;
    const sortVars = s
      ? { orderBy: s.sortColumn, orderSortType: s.sortColumnDirection[s.sortColumn] }
      : {};
    const filters = buildQueueFilters(null, lastSentSearchRef.current);
    getQueueList({
      variables: {
        limit: LIMIT,
        offset: 0,
        type: 'REVIEW',
        filters,
        ...sortVars,
      },
    });
    setOffsetRequestReview(0);
    setOffsetRequestAssign(0);
  }, [dispatch, getQueueList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditFilter = (editFilter: boolean): void => {
    setIsEditFilter(editFilter);
  };

  const handleToggleFilterEvent = (): void => {
    handleEditFilter(false);
    setSelectedFilter(updatedSelectedFilter);
    setFilterEventModalVisible(!filterEventModalVisible);
    setOpenFilter(!openFilter);
  };

  const getFilterApiValue = (filterValues: DynamicValue): any => {
    let reducedFilterValues = {};
    if (filterValues) {
      Object.keys(filterValues).forEach((key: string) => {
        if (Array.isArray(filterValues[key])) {
          let values;
          values = filterValues[key].map((item: SelectedData) => item.id);
          if (key === 'region') {
            values = filterValues[key].map((item: SelectedData) => item.label);
          } else if (key === 'territory') {
            values = filterValues[key].map((item: SelectedData) => item.label);
          }
          if (values && values.length > 0) {
            reducedFilterValues = { ...reducedFilterValues, [key]: [...values] };
          }
        } else if (Array.isArray(filterValues[key]) && filterValues[key]) {
          if (filterValues[key].length > 0) {
            reducedFilterValues = { ...reducedFilterValues, [key]: filterValues[key] };
          }
        } else if (key !== '__typename' && filterValues[key]) {
          reducedFilterValues = { ...reducedFilterValues, [key]: filterValues[key] };
        } else if (key === SHOW_AGENCY_RECORDS || key === SHOW_STRYKER_RECORDS) {
          reducedFilterValues = { ...reducedFilterValues, [key]: filterValues[key] || false };
        }
      });
      const filtersData = { ...reducedFilterValues };
      return filtersData;
    }
    return {};
  };

  const handleRefetchItems = (filter: any): void => {
    getQueueList({
      variables: {
        limit: LIMIT,
        offset: 0,
        type: 'REVIEW',
        filters: filter,
      },
    });
    setOffsetRequestReview(0);
    setOffsetRequestAssign(0);
  };

  const handleFilterEvents = (
    filterValues: Filter | DynamicValue | null,
    filterName?: string
  ): void => {
    // Treat empty objects the same as null — trigger reset
    const hasValidFilters = filterValues && Object.keys(filterValues).length > 0;

    if (!hasValidFilters) {
      resetFilters();
      return;
    }

    const updatedFilterName = filterName || '';
    const filter = {
      filterValues,
      filterName: updatedFilterName,
    };
    setUpdatedSelectedFilter(filter);
    setSelectedFilter(filter);
    if (filterValues) {
      const filters = getFilterApiValue(filterValues);
      setSelectedFilterApiValues(filters);
      applyFilters(filters);
    } else {
      resetFilters();
    }
  };

  const handleEditPress = (filter: FilterPreset, isEditPress: boolean): void => {
    setSelectedFilter(filter);
    setFilterEventModalVisible(!filterEventModalVisible);
    setOpenFilter(!openFilter);
    if (isEditPress) {
      handleEditFilter(true);
    }
  };

  const handleDeleteFilterPreset = (externalId: string): void => {
    const filterExternalId = selectedFilter ? selectedFilter.filterExternalId : null;
    if (filterExternalId && filterExternalId === externalId) {
      resetFilters();
    }
  };

  const handleToggleFilter = (): void => {
    setOpenFilter(!openFilter);
  };
  const filterStore = useSelector(getInventoryReviewFilters);
  useEffect(() => {
    const parsedFilters = JSON.parse(filterStore || '{}');
    // Only use filters if the object has actual filter values (not empty)
    if (Object.keys(parsedFilters).length > 0 && parsedFilters.filterValues) {
      setAppliedFilters(parsedFilters.filterValues);
      setSelectedFilter(parsedFilters);
      setSelectedFilterApiValues(parsedFilters.filterValues);
    } else {
      // No saved filters - reset to defaults will be handled by main useEffect
      setAppliedFilters(null);
      setSelectedFilter(null);
      setSelectedFilterApiValues(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <IconSettings iconPath="/icons">
      <InventoryQueueHeader
        handleToggleFilter={handleToggleFilter}
        handleSearch={handleSearch}
        filterPresets={filterData}
        resetFilters={resetFilters}
        onEditPress={handleEditPress}
        handleFilterEvents={handleFilterEvents}
        selectedFilterApiValues={selectedFilterApiValues}
        totalFilterResult={inventoryQueueList.length}
        searchValue={searchValue}
      />
      <SnackBar open={open} notification={notification} />
      {appliedFilters && !isEditFilter && (
        <FilterTagReviewRequest selectedFilter={appliedFilters} handleFilter={applyFilters} />
      )}
      <div style={{ overflow: 'auto' }}>
        {loading || isLoading ? (
          <Spinner size="small" variant="base" assistiveText={{ label: 'Loading...' }} />
        ) : (
          <DataTable
            items={itemsWithSubmissionStatus || []}
            id="Inventory-Queue-DataTable"
            className="font-12"
            fixedLayout
            // fixedHeader
            joined
            assistiveText={{
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
            }}
            keyboardNavigation
            onSort={handleSort}
          >
            {[
              <DataTableColumn
                key="submittedDate"
                label="Submit Date"
                property="submittedDate"
                isSorted={sortObj?.sortColumn === 'submittedDate'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.submittedDate}
              >
                <InventoryRequestDateTImeCell dateFor="submitted" />
              </DataTableColumn>,
              <DataTableColumn
                key="eventId"
                label="Record"
                property="eventId"
                isSorted={sortObj?.sortColumn === 'eventId'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.eventId}
              >
                <InventoryRequestIdCell action={action} />
              </DataTableColumn>,
              <DataTableColumn
                key="type"
                label="Type"
                property="type"
                isSorted={sortObj?.sortColumn === 'type'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.type}
              />,
              <DataTableColumn
                key="eventStatus"
                label="Status"
                property="eventStatus"
                isSorted={sortObj?.sortColumn === 'eventStatus'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.eventStatus}
              >
                <InventoryRequestStatusCell />
              </DataTableColumn>,
              <DataTableColumn
                key="accountName"
                label="Account"
                property="accountName"
                isSorted={sortObj?.sortColumn === 'accountName'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.accountName}
                width="1rem"
              >
                <AccountCell />
              </DataTableColumn>,
              <DataTableColumn
                key="shippingPostalcode"
                label="Zip Code"
                property="shippingPostalcode"
                isSorted={sortObj?.sortColumn === 'shippingPostalcode'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.shippingPostalcode}
              />,
              <DataTableColumn
                key="salesRep"
                label="Sales Rep"
                property="salesRep"
                isSorted={sortObj?.sortColumn === 'salesRep'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.salesRep}
              />,
              <DataTableColumn
                key="territoryNumber"
                label="Territory"
                property="territoryNumber"
                isSorted={sortObj?.sortColumn === 'territoryNumber'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.territoryNumber}
              />,
              <DataTableColumn
                key="userHoldReasons"
                label="Hold Reason"
                property="userHoldReasons"
                isSorted={sortObj?.sortColumn === 'userHoldReasons'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.userHoldReasons}
              />,
              <DataTableColumn
                key="serviceLevel"
                label="Service Level"
                property="serviceLevel"
                isSorted={sortObj?.sortColumn === 'serviceLevel'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.serviceLevel}
              />,
              <DataTableColumn
                key="needByDate"
                label="Need by Date"
                property="needByDate"
                isSorted={sortObj?.sortColumn === 'needByDate'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.needByDate}
              >
                <InventoryRequestDateTImeCell dateFor="neededBy" />
              </DataTableColumn>,
              <DataTableColumn
                key="assignedUserName"
                label="Assign To"
                width="160px"
                property="assignedUserName"
                isSorted={sortObj?.sortColumn === 'assignedUserName'}
                sortable
                sortDirection={sortObj?.sortColumnDirection.assignedUserName}
              >
                <InventoryRequestsAssignDataCell
                  // action={action}
                  openSnackBar={openSnackBar}
                  refetch={refetch}
                />
              </DataTableColumn>,
              <DataTableColumn key="assigned" label="" width="50px">
                <ResetCell handleReset={handleReset} />
              </DataTableColumn>,
            ]}
          </DataTable>
        )}
        {(inventoryQueueList.length > 0 || offsetRequestAssign > 0) &&
          action.toUpperCase() === 'ASSIGN' && (
          <div className="slds-grid slds-grid_align-center slds-grid_vertical-align-center slds-p-top_medium">
            <div className="slds-col">
              <Button
                assistiveText={{ icon: 'Icon Bare Small' }}
                iconCategory="utility"
                iconName="chevronleft"
                iconSize="small"
                disabled={!(offsetRequestAssign > 0)}
                iconVariant="bare"
                // will fetch the previous events data
                onClick={(): void => handlePaginationButtonClicked(PREV_DATA)}
                variant="icon"
              />
            </div>
            <div className="slds-col slds-p-left_medium slds-p-right_medium">
              <span>{`${offsetRequestAssign + 1} - ${offsetRequestAssign + LIMIT}`}</span>
            </div>
            <div className="slds-col">
              <Button
                assistiveText={{ icon: 'Icon Bare Small' }}
                iconCategory="utility"
                iconName="chevronright"
                iconSize="small"
                iconVariant="bare"
                disabled={inventoryQueueList.length < LIMIT}
                // will fetch the next events data
                onClick={(): void => handlePaginationButtonClicked(NEXT_DATA)}
                variant="icon"
              />
            </div>
          </div>
        )}
        {(inventoryQueueList.length > 0 || offsetRequestReview > 0) &&
          action.toUpperCase() === 'REVIEW' && (
          <div className="slds-grid slds-grid_align-center slds-grid_vertical-align-center slds-p-top_medium">
            <div className="slds-col">
              <Button
                assistiveText={{ icon: 'Icon Bare Small' }}
                iconCategory="utility"
                iconName="chevronleft"
                iconSize="small"
                disabled={!(offsetRequestReview > 0)}
                iconVariant="bare"
                // will fetch the previous events data
                onClick={(): void => handlePaginationButtonClicked(PREV_DATA)}
                variant="icon"
              />
            </div>
            <div className="slds-col slds-p-left_medium slds-p-right_medium">
              <span>{`${offsetRequestReview + 1} - ${offsetRequestReview + LIMIT}`}</span>
            </div>
            <div className="slds-col">
              <Button
                assistiveText={{ icon: 'Icon Bare Small' }}
                iconCategory="utility"
                iconName="chevronright"
                iconSize="small"
                iconVariant="bare"
                disabled={inventoryQueueList.length < LIMIT}
                // will fetch the next events data
                onClick={(): void => handlePaginationButtonClicked(NEXT_DATA)}
                variant="icon"
              />
            </div>
          </div>
        )}
      </div>
      {openFilter && (
        <InventoryQueueFilter
          openFilter={openFilter}
          handleToggleFilter={handleToggleFilter}
          applyFilters={applyFilters}
          selectedFilter={selectedFilter}
          handleToggleFilterEvent={handleToggleFilterEvent}
          handleFilterEvents={handleFilterEvents}
          handleEditFilter={handleEditFilter}
          refetchFilterPresets={refetchFilterPresets}
          isEditFilter={isEditFilter}
          handleClearFilter={handleDeleteFilterPreset}
        />
      )}
    </IconSettings>
  );
};

export default InventoryQueue;
